import React, {useMemo, useState} from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import {associates, onboardingSteps} from '../onboardingData';

const stages = [
  {id: 1, en: 'Getting Started', es: 'Primeros pasos'},
  {id: 2, en: 'Licensing & Requirements', es: 'Licencias y requisitos'},
  {id: 3, en: 'First 30 Days', es: 'Primeros 30 días'}
];

const copy = {
  en: {title: 'Your onboarding journey', helper: 'Take it one step at a time. Your recruiter is here to help.', complete: 'steps complete', remaining: 'Estimated time remaining', min: 'min', view: 'View step', documents: 'documents', notStarted: 'Not started', inProgress: 'In progress', done: 'Done', myAssociates: 'My associates', allAssociates: 'All associates', currentStep: 'Current step', days: 'days since invited', reminder: 'Send reminder', recruiter: 'Recruiter', allRecruiters: 'All recruiters', reminderToast: 'Reminder sent to'},
  es: {title: 'Tu camino de incorporación', helper: 'Avanza paso a paso. Tu reclutador está aquí para ayudarte.', complete: 'pasos completados', remaining: 'Tiempo estimado restante', min: 'min', view: 'Ver paso', documents: 'documentos', notStarted: 'No iniciado', inProgress: 'En progreso', done: 'Completado', myAssociates: 'Mis asociados', allAssociates: 'Todos los asociados', currentStep: 'Paso actual', days: 'días desde la invitación', reminder: 'Enviar recordatorio', recruiter: 'Reclutador', allRecruiters: 'Todos los reclutadores', reminderToast: 'Recordatorio enviado a'}
};

function getProgress(userId, progress) {
  return onboardingSteps.map((step) => progress.find((item) => item.userId === userId && item.stepId === step.id) || {
    userId, stepId: step.id, status: 'Not started', completedAt: null, requirementsChecked: []
  });
}

function StepList({language, progress, onOpen}) {
  const [expanded, setExpanded] = useState(null);
  const text = copy[language];
  return (
    <div className="onboarding-stepper">
      {stages.map((stage) => {
        const steps = onboardingSteps.filter((step) => step.stage === stage.id);
        const isComplete = steps.every((step) => progress.find((item) => item.stepId === step.id)?.status === 'Done');
        return (
          <section className="onboarding-stage" key={stage.id}>
            <h2><span className={isComplete ? 'stage-done' : ''}><SafeIcon icon={isComplete ? FiIcons.FiCheck : FiIcons.FiFlag} /></span>{language === 'es' ? stage.es : stage.en}</h2>
            {steps.map((step) => {
              const item = progress.find((entry) => entry.stepId === step.id);
              const opened = expanded === step.id;
              const title = step[`title${language === 'es' ? 'ES' : 'EN'}`];
              const description = step[`description${language === 'es' ? 'ES' : 'EN'}`];
              return (
                <article className={`onboarding-step ${item.status.toLowerCase().replace(' ', '-')} ${opened ? 'expanded' : ''}`} key={step.id}>
                  <button type="button" className="step-summary" onClick={() => setExpanded(opened ? null : step.id)}>
                    <span className="step-marker">{item.status === 'Done' ? <SafeIcon icon={FiIcons.FiCheck} /> : step.order}</span>
                    <span><strong>{title}</strong><small>{description}</small></span>
                    <SafeIcon icon={opened ? FiIcons.FiChevronUp : FiIcons.FiChevronDown} />
                  </button>
                  <div className="step-extra">
                    <span className={`onboarding-status ${item.status.toLowerCase().replace(' ', '-')}`}>{text[item.status === 'Done' ? 'done' : item.status === 'In progress' ? 'inProgress' : 'notStarted']}</span>
                    <span><SafeIcon icon={FiIcons.FiFileText} /> {step.requirementsEN.length} {text.documents}</span>
                    <button type="button" onClick={(event) => {event.stopPropagation(); onOpen(step.id);}}>{text.view} <SafeIcon icon={FiIcons.FiArrowRight} /></button>
                  </div>
                </article>
              );
            })}
          </section>
        );
      })}
    </div>
  );
}

function AssociateList({language, progress, admin}) {
  const [recruiter, setRecruiter] = useState('');
  const [toast, setToast] = useState('');
  const text = copy[language];
  const visible = recruiter ? associates.filter((associate) => associate.recruiterId === recruiter) : associates;
  const recruiters = [...new Map(associates.map((item) => [item.recruiterId, item.recruiter])).entries()];

  const sendReminder = (event, name) => {
    event.preventDefault();
    event.stopPropagation();
    setToast(`${text.reminderToast} ${name}`);
    window.setTimeout(() => setToast(''), 2200);
  };

  return (
    <div className="associate-list-section">
      <div className="associate-list-heading">
        <div><h1>{admin ? text.allAssociates : text.myAssociates}</h1><p>{admin ? 'Onboarding progress across every recruiter.' : 'See where each new associate is in their journey.'}</p></div>
        {admin && <label>{text.recruiter}<select value={recruiter} onChange={(event) => setRecruiter(event.target.value)}><option value="">{text.allRecruiters}</option>{recruiters.map(([id, name]) => <option key={id} value={id}>{name}</option>)}</select></label>}
      </div>
      <div className="associate-list">
        {visible.map((associate) => {
          const items = getProgress(associate.id, progress);
          const completed = items.filter((item) => item.status === 'Done').length;
          const current = items.find((item) => item.status === 'In progress') || items.find((item) => item.status === 'Not started');
          const step = onboardingSteps.find((item) => item.id === current?.stepId);
          return (
            <article className="associate-row" key={associate.id}>
              <div className="associate-name"><span>{associate.name.split(' ').map((part) => part[0]).join('')}</span><div><strong>{associate.name}</strong><small>{associate.country} · {Math.max(1, Math.ceil((Date.now() - new Date(associate.invitedAt)) / 86400000))} {text.days}</small></div></div>
              {admin && <span className="associate-recruiter">{associate.recruiter}</span>}
              <div className="associate-progress"><div><span>{completed}/9</span><small>{step?.[`title${language === 'es' ? 'ES' : 'EN'}`] || '—'}</small></div><i><b style={{width: `${(completed / 9) * 100}%`}} /></i></div>
              <button type="button" className="reminder-button" onClick={(event) => sendReminder(event, associate.name)}><SafeIcon icon={FiIcons.FiSend} /> {text.reminder}</button>
            </article>
          );
        })}
      </div>
      {toast && <div className="onboarding-toast" role="status">{toast}</div>}
    </div>
  );
}

function OnboardingPage({user, language, progress, onOpenStep}) {
  const userProgress = useMemo(() => getProgress(user.id, progress), [user.id, progress]);
  const text = copy[language];
  if (user.role === 'recruiter' || user.role === 'admin') return <div className="page-content onboarding-page"><AssociateList language={language} progress={progress} admin={user.role === 'admin'} /></div>;

  const completed = userProgress.filter((item) => item.status === 'Done').length;
  const remaining = onboardingSteps.filter((step) => !userProgress.find((item) => item.stepId === step.id)?.completedAt).reduce((total, step) => total + step.estimatedMinutes, 0);
  return (
    <div className="page-content onboarding-page">
      <header className="onboarding-hero"><span className="eyebrow">GFI INTERNATIONAL</span><h1>{text.title}</h1><p>{text.helper}</p><div className="onboarding-progress-copy"><strong>{completed} {text.complete}</strong><span>{text.remaining}: {remaining} {text.min}</span></div><div className="onboarding-progress"><span style={{width: `${(completed / 9) * 100}%`}} /></div></header>
      <StepList language={language} progress={userProgress} onOpen={onOpenStep} />
    </div>
  );
}

export default OnboardingPage;